import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Flag, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button, Field, Sheet, Textarea } from '@/components/ui';
import { ReportReason, reportReasonLabels, type ReportReceipt } from '@/lib/types';

/**
 * Reporting a listing.
 *
 * Open to signed-out visitors on purpose. The person who recognises a photograph lifted from another
 * advertisement, or a price that cannot be real, is very often the one who has not made an account —
 * and a report form behind a sign-in wall is a report that never arrives.
 *
 * The reasons are ordered by how much harm they describe rather than by the numbers behind them.
 * Somebody reaching for this button is usually looking at a fraud, and making them read past
 * "wrong category" to find it is the wrong way round.
 */
const REASONS = [
  ReportReason.Fraud,
  ReportReason.MisleadingPrice,
  ReportReason.OffensiveContent,
  ReportReason.Spam,
  ReportReason.DuplicateListing,
  ReportReason.WrongCategory,
  ReportReason.SoldAlready,
  ReportReason.Other,
] as const;

export function ReportListingDialog({
  listingId,
  open,
  onClose,
}: {
  listingId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<number>(ReportReason.Fraud);
  const [details, setDetails] = useState('');
  const [sent, setSent] = useState<ReportReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reopening starts a fresh report. Without this, someone who reports one problem and comes back
  // about another finds the first one's text still in the box.
  useEffect(() => {
    if (open) {
      setReason(ReportReason.Fraud);
      setDetails('');
      setSent(null);
      setError(null);
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api<ReportReceipt>(`/api/listings/${listingId}/reports`, {
        method: 'POST',
        body: { reason, details: details.trim() || null },
      }),
    onSuccess: (receipt) => setSent(receipt),
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Prijava nije poslata. Pokušajte ponovo.'),
  });

  const needsDetails = reason === ReportReason.Other && details.trim().length === 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={sent ? 'Prijava je primljena' : 'Prijavi oglas'}
      footer={
        sent ? (
          <Button className="w-full" onClick={onClose}>
            Zatvori
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose}>
              Odustani
            </Button>
            <Button
              className="flex-1"
              onClick={() => submit.mutate()}
              disabled={submit.isPending || needsDetails}
            >
              <Flag size={15} />
              Prijavi
            </Button>
          </div>
        )
      }
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <ShieldCheck size={40} className="text-success" />
          <p className="text-sm text-body">
            {sent.alreadyReported
              ? 'Već ste prijavili ovaj oglas. Prijava je i dalje otvorena.'
              : 'Hvala. Pregledaćemo oglas u najkraćem roku.'}
          </p>
          <p className="text-xs text-faint">
            Ne šaljemo obaveštenje o ishodu, ali svaka prijava se pregleda.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Recite nam šta nije u redu sa ovim oglasom. Prijava je anonimna za prodavca.
          </p>

          <fieldset className="space-y-1.5">
            <legend className="mb-2 text-sm font-semibold text-body">Razlog</legend>

            {REASONS.map((value) => (
              <label
                key={value}
                className="press flex cursor-pointer items-center gap-3 rounded-xl border border-default px-3 py-2.5 transition has-[:checked]:border-accent has-[:checked]:bg-accent-soft"
              >
                <input
                  type="radio"
                  name="report-reason"
                  className="accent-[var(--color-accent)]"
                  checked={reason === value}
                  onChange={() => setReason(value)}
                />
                <span className="text-sm text-body">{reportReasonLabels[value]}</span>
              </label>
            ))}
          </fieldset>

          <Field
            label="Opis"
            hint={
              reason === ReportReason.Other
                ? 'Obavezno kada birate „Ostalo“.'
                : 'Neobavezno, ali pomaže.'
            }
          >
            <Textarea
              rows={4}
              value={details}
              maxLength={2000}
              onChange={(event) => setDetails(event.target.value)}
              placeholder="Npr. iste fotografije se pojavljuju na više oglasa."
            />
          </Field>

          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      )}
    </Sheet>
  );
}
